import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, Case, CaseStatus, DocumentPage, Note, NotePage } from "../lib/api";
import DocDropzone from "../components/DocDropzone";
import DocList, { DEFAULT_DOC_FILTERS, DOC_PAGE_SIZE, DocFilters } from "../components/DocList";
import OneDriveImport from "../components/OneDriveImport";
import ChatPane from "../components/ChatPane";
import TimelineView from "../components/TimelineView";
import NotesView, { NoteDraft } from "../components/NotesView";
import MembersView from "../components/MembersView";
import VideosView from "../components/VideosView";
import CaseEdgeRail from "../components/CaseEdgeRail";
import { useAuth } from "../lib/auth";
import { useChatStore } from "../lib/chatStore";
// Roles allowed to edit case metadata (name, description). Mirrors the
// server-side guard in `engine/app/api/cases.py::update_case`.
const CASE_EDIT_ROLES = new Set(["owner", "admin", "lawyer"]);

type Tab = "chat" | "docs" | "timeline" | "notes" | "videos" | "members";

const TAB_VALUES: readonly Tab[] = [
  "chat",
  "docs",
  "timeline",
  "notes",
  "videos",
  "members",
] as const;

function isTab(value: string | null): value is Tab {
  return value !== null && (TAB_VALUES as readonly string[]).includes(value);
}

export default function CaseView() {
  const { caseId } = useParams();
  const id = Number(caseId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [kase, setKase] = useState<Case | null>(null);
  const [docPage, setDocPage] = useState<DocumentPage | null>(null);
  const [docBusy, setDocBusy] = useState(false);
  const [docFilters, setDocFilters] = useState<DocFilters>(DEFAULT_DOC_FILTERS);
  // The active workspace tab lives in the URL (?tab=...) so the edge rail
  // can deeplink straight into a specific pane from outside the case (e.g.
  // from the Chat Search page) and so browser back/forward stepping through
  // tab swaps Just Works without us tracking a parallel history stack.
  const tabParam = searchParams.get("tab");
  const tab: Tab = isTab(tabParam) ? tabParam : "chat";
  const setTab = useCallback(
    (next: Tab | ((current: Tab) => Tab)) => {
      // Mirrors the React useState setter contract (accepts either a value
      // or an updater) so existing callers like the note overlay logic —
      // which need to read the *current* tab to remember where to return —
      // keep working unchanged. The URL is the source of truth, so we
      // resolve the previous tab from the latest search params.
      // `replace` so an in-page tab swap doesn't pile entries onto the
      // browser history; pressing Back from the case should still return
      // the user to wherever they came from before opening the case.
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          const prevParam = params.get("tab");
          const current: Tab = isTab(prevParam) ? prevParam : "chat";
          const resolved = typeof next === "function" ? next(current) : next;
          if (resolved === "chat") params.delete("tab");
          else params.set("tab", resolved);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // "+ Upload documents" (rail / mobile header) links to ?tab=docs&upload=1.
  // We consume the flag into a counter the dropzone watches to open its
  // file picker, then strip it so a refresh doesn't re-open the picker.
  const uploadParam = searchParams.get("upload");
  const [pickerRequest, setPickerRequest] = useState(0);
  useEffect(() => {
    if (uploadParam !== "1") return;
    setPickerRequest((n) => n + 1);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete("upload");
        return params;
      },
      { replace: true },
    );
  }, [uploadParam, setSearchParams]);
  // Number of documents in the upload that just finished; drives the
  // "Upload complete → Start asking questions" prompt.
  const [uploadedCount, setUploadedCount] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [timelineRefresh, setTimelineRefresh] = useState(0);
  const [notesRefresh, setNotesRefresh] = useState(0);
  // Mirror of the case's notes so chat/timeline can show "Saved" badges for
  // Q&As and events the user has already captured. Refreshed alongside the
  // Notes pane via `notesRefresh`.
  const [notes, setNotes] = useState<Note[]>([]);
  // When chat/timeline jump to the Notes tab to open a specific saved note,
  // the target id flows through here so NotesView can select it.
  const [pendingNoteSelectId, setPendingNoteSelectId] =
    useState<number | null>(null);
  // When the user highlights text in a chat answer and clicks "Save
  // selection to note", we hand a pre-built NoteDraft to NotesView so it
  // opens its editor pre-populated and the user can set tags / category
  // before saving. NotesView clears this via `onPendingConsumed`.
  const [pendingNoteDraft, setPendingNoteDraft] =
    useState<NoteDraft | null>(null);
  // Tab the user was on when they triggered an "open note" overlay from
  // chat or timeline — we briefly switch to the Notes tab so the modal
  // mounts with the right context, then restore this tab when the modal
  // closes so the user lands back where they actually were. Null when the
  // modal was opened from the Notes tab itself (no restoration needed).
  const [noteOverlayReturnTab, setNoteOverlayReturnTab] =
    useState<Tab | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const selectChatConversation = useChatStore((s) => s.selectConversation);
  const authUser = useAuth((s) => s.user);
  const canEditCase = !!authUser && CASE_EDIT_ROLES.has(authUser.role);

  const loadNotes = useCallback(async () => {
    try {
      // We only need this list for the savedNotesIndex (chat/timeline
      // "Saved" badge → open-note shortcut). The endpoint's `limit=200`
      // cap is plenty to cover the cases we've actually seen; if it ever
      // becomes a real ceiling we'll trade this for a dedicated
      // /notes/saved-index endpoint.
      const data = await api.get<NotePage>(`/cases/${id}/notes?scope=all&limit=200`);
      setNotes(data.items);
    } catch {
      /* best-effort: the Notes pane will surface its own error */
    }
  }, [id]);

  useEffect(() => { void loadNotes(); }, [loadNotes, notesRefresh]);

  // Index "save once" sources (chat queries, timeline events) to the note
  // they were captured into, so the source surface can flip its button to
  // "Saved · Open" and route a click straight back to that note.
  // Snippet/selection notes (kind === "selection") are intentionally NOT
  // indexed here — they're per-highlight captures that should never claim
  // to be "the saved version" of the underlying Q&A or event, so the
  // turn's full-answer button keeps tracking its own answer note.
  const savedNotesIndex = useMemo(() => {
    const m = new Map<string, number>();
    for (const n of notes) {
      if (!n.is_owner) continue;
      const ref = n.source_ref as
        | { query_id?: unknown; event_id?: unknown; kind?: unknown }
        | null;
      if (ref?.kind === "selection") continue;
      if (n.source_type === "chat" && typeof ref?.query_id === "number") {
        m.set(`chat:${ref.query_id}`, n.id);
      }
      if (n.source_type === "timeline" && typeof ref?.event_id === "number") {
        m.set(`timeline:${ref.event_id}`, n.id);
      }
    }
    return m;
  }, [notes]);

  const saveNoteDirect = useCallback(
    async (draft: NoteDraft): Promise<number> => {
      const created = await api.post<Note>(`/cases/${id}/notes`, {
        title: draft.title,
        body: draft.body,
        category: draft.category,
        tags: draft.tags ?? [],
        source_type: draft.source_type ?? "manual",
        source_ref: draft.source_ref,
        share_with: [],
      });
      setNotes((prev) => [created, ...prev.filter((n) => n.id !== created.id)]);
      setNotesRefresh((n) => n + 1);
      return created.id;
    },
    [id],
  );

  const openSavedNote = useCallback(
    (noteId: number) => {
      // Remember where we came from so closing the modal returns the user
      // to the chat/timeline they were reading — opening a saved note
      // shouldn't strand them on the Notes tab.
      if (tab !== "notes") setNoteOverlayReturnTab(tab);
      setPendingNoteSelectId(noteId);
      setTab("notes");
    },
    [tab],
  );

  // "Capture this highlighted snippet as a new note" — invoked from the
  // chat selection popover. We jump to the Notes tab and hand the editor
  // the pre-built draft so the user can set tags / category before saving.
  const captureNoteDraft = useCallback(
    (draft: NoteDraft) => {
      if (tab !== "notes") setNoteOverlayReturnTab(tab);
      setPendingNoteDraft(draft);
      setTab("notes");
    },
    [tab],
  );

  // Fired by NotesView whenever its editor modal closes (Save, Close,
  // Delete, Esc — all paths). If we stashed a return tab when the modal
  // was opened from outside, restore it now so the user is back where
  // they were before clicking the "Saved" badge / "Open in editor" link.
  // We only restore if the user is still on the Notes tab — if they
  // manually picked a different tab while the modal was open, respect
  // that choice and don't override it.
  const handleNoteModalClosed = useCallback(() => {
    setNoteOverlayReturnTab((prev) => {
      if (prev && tab === "notes") setTab(prev);
      return null;
    });
  }, [tab]);

  // From a chat-sourced note's "Open in chat" button: switch tabs and
  // hydrate the originating conversation if we know which one it was.
  const openChatConversation = useCallback(
    (conversationId: number) => {
      void selectChatConversation(id, conversationId);
      setTab("chat");
    },
    [id, selectChatConversation, setTab],
  );

  // Bumping the refresh key whenever the user switches INTO the timeline tab
  // means a re-fetch is guaranteed even if a save happened from another page
  // (or another browser tab) — much more bullet-proof than relying purely on
  // the post-save callback.
  useEffect(() => {
    if (tab === "timeline") setTimelineRefresh((n) => n + 1);
  }, [tab]);

  // Build the documents list URL for the current filters/page. Centralised
  // so the initial load, the on-demand refresh, and the polling loop all
  // hit exactly the same query — otherwise they'd race and overwrite each
  // other with subtly different snapshots.
  const docsQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (docFilters.q.trim()) p.set("q", docFilters.q.trim());
    if (docFilters.status !== "all") p.set("status", docFilters.status);
    p.set("limit", String(DOC_PAGE_SIZE));
    p.set("offset", String((docFilters.page - 1) * DOC_PAGE_SIZE));
    return p.toString();
  }, [docFilters]);

  // Background refresh: re-fetch BOTH the case (for the absolute header
  // counts) and the current docs page (for the visible rows). Used by the
  // polling loop and after delete/retry/upload — the case fetch is what
  // makes "Documents 4 · Ready 4" tick up as ingest finishes, regardless
  // of whatever search/status filter the user has applied to the table.
  const refreshDocs = useCallback(async (): Promise<DocumentPage | null> => {
    try {
      const [c, dp] = await Promise.all([
        api.get<Case>(`/cases/${id}`),
        api.get<DocumentPage>(`/cases/${id}/documents?${docsQuery}`),
      ]);
      setKase(c);
      setDocPage(dp);
      return dp;
    } catch (e: any) {
      setErr(e?.message ?? "Failed to load documents");
      return null;
    }
  }, [id, docsQuery]);

  // After deletes / retries / uploads we want both the case meta (for
  // document_count etc.) and the docs page to refresh together.
  const refresh = useCallback(async () => {
    try {
      const [c, dp] = await Promise.all([
        api.get<Case>(`/cases/${id}`),
        api.get<DocumentPage>(`/cases/${id}/documents?${docsQuery}`),
      ]);
      setKase(c);
      setDocPage(dp);
    } catch (e: any) {
      setErr(e?.message ?? "Failed to load case");
    }
  }, [id, docsQuery]);

  // Initial case load — separate from the docs effect below so a filter
  // change doesn't refetch the entire case record.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await api.get<Case>(`/cases/${id}`);
        if (!cancelled) setKase(c);
      } catch (e: any) {
        if (!cancelled) setErr(e?.message ?? "Failed to load case");
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // (Re)load the documents page whenever the filters, page, or case change.
  useEffect(() => {
    let cancelled = false;
    setDocBusy(true);
    (async () => {
      try {
        const dp = await api.get<DocumentPage>(`/cases/${id}/documents?${docsQuery}`);
        if (!cancelled) setDocPage(dp);
      } catch (e: any) {
        if (!cancelled) setErr(e?.message ?? "Failed to load documents");
      } finally {
        if (!cancelled) setDocBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, docsQuery]);

  // Poll while ANY document in the case is still processing/queued — even
  // ones that aren't on the visible page or that are hidden by the active
  // search/status filter. We use the case-level pending count (which
  // ignores filters) so a search like "report" doesn't accidentally stop
  // the poller while ingest is still running on docs that don't match.
  useEffect(() => {
    if (!kase || kase.pending_document_count <= 0) return;
    const t = setInterval(() => { void refreshDocs(); }, 2500);
    return () => clearInterval(t);
  }, [kase, refreshDocs]);

  // If the user navigates to a page that no longer exists (e.g. they
  // deleted the last item on page 3), snap back to page 1 so they aren't
  // staring at "No results" by accident.
  useEffect(() => {
    if (!docPage) return;
    if (docFilters.page > 1 && docPage.items.length === 0 && docPage.total > 0) {
      setDocFilters((f) => ({ ...f, page: 1 }));
    }
  }, [docPage, docFilters.page]);

  if (err) return <div className="alert alert--error">{err}</div>;
  if (!kase) return <div className="muted">Loading case…</div>;

  // Header / tab badge always shows the absolute case totals so they
  // don't appear to "shrink" as the user types into the documents search
  // box (which only narrows the table, not the case).
  const totalDocs = kase.document_count;
  const readyCount = kase.ready_document_count;

  // Chat is height-bound so the composer can stay pinned to the bottom
  // and the log scrolls inside the pane. The Documents, Notes, Timeline,
  // and Members tabs are just long lists — let the natural page scroll
  // handle them (no inner indented scrollbar) so the bar lives at the
  // viewport edge.
  const wantsPageScroll =
    tab === "docs" ||
    tab === "notes" ||
    tab === "timeline" ||
    tab === "videos" ||
    tab === "members";

  return (
    <div className={`case-view${wantsPageScroll ? " case-view--scroll" : ""}`}>
      <div className="case-view__head">
        <div className="case-view__head-main">
          <div className="case-view__title-block">
            <div className="case-view__title-row">
              <h1>{kase.name}</h1>
              {(kase.description || canEditCase) && (
                <button
                  type="button"
                  className="case-view__info-btn"
                  onClick={() => setShowInfo(true)}
                  aria-label={canEditCase ? "Show or edit case details" : "Show case details"}
                  title={canEditCase ? "Case details · click to edit" : "Case details"}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </button>
              )}
              <div className="case-view__meta">
                {kase.matter_number && <span className="case-view__meta-pill">Matter #{kase.matter_number}</span>}
                <span className="case-view__meta-pill">{readyCount} ready for grounded answers</span>
              </div>
            </div>
            <div className="case-view__subtitle">
              Date Added: {formatDateAdded(kase.created_at)}
            </div>
          </div>
          <div className="case-view__controls">
            <div className="case-view__summary" aria-label="Case summary">
              <div className="case-view__summary-item">
                <span className="case-view__summary-label">Documents</span>
                <strong>{totalDocs}</strong>
              </div>
              <div className="case-view__summary-item">
                <span className="case-view__summary-label">Ready</span>
                <strong>{readyCount}</strong>
              </div>
              <div className="case-view__summary-item">
                <span className="case-view__summary-label">Status</span>
                <span className={`badge badge--${kase.status}`}>{kase.status}</span>
              </div>
            </div>
            <Link
              to={`/cases/${id}?tab=docs&upload=1`}
              className="btn btn--primary btn--sm case-view__upload-mobile"
            >
              + Upload documents
            </Link>
          </div>
        </div>
      </div>

      {/* Rail rendered before the workspace so on phones (where it goes
          static) it lands directly under the case header instead of
          below the active tab pane. On desktop it's `position: fixed`
          so DOM position is irrelevant. */}
      <CaseEdgeRail
        caseId={id}
        activeTab={tab}
        totalDocs={totalDocs}
        memberCount={kase.member_count}
        videoCount={kase.video_count}
      />

      <div className="case-view__workspace">
        {/* Each pane stays mounted; we only hide the inactive ones so chat
            state, scroll position, and draft input survive tab switches. */}
        <div className="case-view__body">
          <div className="case-view__pane" hidden={tab !== "chat"}>
            <ChatPane
              caseId={id}
              readyCount={readyCount}
              active={tab === "chat"}
              onTimelineSaved={() => setTimelineRefresh((n) => n + 1)}
              savedNotesIndex={savedNotesIndex}
              onSaveNote={saveNoteDirect}
              onOpenNote={openSavedNote}
              onCaptureSelection={captureNoteDraft}
            />
          </div>
          <div className="case-view__pane" hidden={tab !== "timeline"}>
            <TimelineView
              caseId={id}
              refreshKey={timelineRefresh}
            />
          </div>
          <div className="case-view__pane" hidden={tab !== "notes"}>
            <NotesView
              caseId={id}
              refreshKey={notesRefresh}
              pendingSelectId={pendingNoteSelectId}
              onPendingSelectConsumed={() => setPendingNoteSelectId(null)}
              pendingDraft={pendingNoteDraft}
              onPendingConsumed={() => setPendingNoteDraft(null)}
              onOpenChat={openChatConversation}
              onModalClosed={handleNoteModalClosed}
            />
          </div>
          <div className="case-view__pane" hidden={tab !== "docs"}>
            <div className="docs-tab">
              <DocDropzone
                caseId={id}
                openPickerRequest={pickerRequest}
                onUploaded={(docs) => {
                  // After upload, snap back to page 1 with no filters so the
                  // user actually sees the docs they just uploaded ticking
                  // through queued → processing → ready.
                  setDocFilters(DEFAULT_DOC_FILTERS);
                  void refresh();
                  if (docs.length) setUploadedCount(docs.length);
                }}
              />
              <OneDriveImport
                caseId={id}
                onImported={() => {
                  // Imports land as new documents/videos in the background;
                  // snap to the default view and refresh so they show up
                  // ticking through queued → processing → ready.
                  setDocFilters(DEFAULT_DOC_FILTERS);
                  void refresh();
                }}
              />
              <DocList
                caseId={id}
                page={docPage}
                filters={docFilters}
                pageSize={DOC_PAGE_SIZE}
                busy={docBusy}
                onFiltersChange={(patch) =>
                  setDocFilters((f) => {
                    const next = { ...f, ...patch };
                    if (("q" in patch || "status" in patch) && !("page" in patch)) {
                      next.page = 1;
                    }
                    return next;
                  })
                }
                onChanged={refresh}
              />
            </div>
          </div>
          <div className="case-view__pane" hidden={tab !== "videos"}>
            <VideosView
              caseId={id}
              active={tab === "videos"}
              onChanged={() => {
                // Uploads/deletes change video_count — refresh the case
                // record so the rail badge stays accurate.
                void api
                  .get<Case>(`/cases/${id}`)
                  .then(setKase)
                  .catch(() => {/* non-fatal; the panel surfaces its own error */});
              }}
            />
          </div>
          <div className="case-view__pane" hidden={tab !== "members"}>
            <MembersView
              caseId={id}
              kase={kase}
              onChanged={() => {
                // The membership change altered member_count and possibly
                // my_role (e.g. user just made themselves lead). Refetch
                // the case so the rail badge + manage gating stay correct.
                void api
                  .get<Case>(`/cases/${id}`)
                  .then(setKase)
                  .catch(() => {/* non-fatal; the panel surfaces its own error */});
              }}
            />
          </div>
        </div>
      </div>

      {uploadedCount !== null && (
        <UploadCompleteModal
          count={uploadedCount}
          totalDocs={totalDocs}
          readyCount={readyCount}
          onAsk={() => {
            setUploadedCount(null);
            setTab("chat");
          }}
          onClose={() => setUploadedCount(null)}
        />
      )}

      {showInfo && (
        <CaseInfoModal
          caseId={id}
          name={kase.name}
          description={kase.description ?? ""}
          matterNumber={kase.matter_number}
          status={kase.status}
          documentCount={totalDocs}
          readyCount={readyCount}
          canEdit={canEditCase}
          onUpdated={(updated) => setKase(updated)}
          onClose={() => setShowInfo(false)}
        />
      )}
    </div>
  );
}

function formatDateAdded(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function UploadCompleteModal({
  count,
  totalDocs,
  readyCount,
  onAsk,
  onClose,
}: {
  count: number;
  totalDocs: number;
  readyCount: number;
  onAsk: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const processing = Math.max(0, totalDocs - readyCount);

  return (
    <div
      className="modal no-print"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-complete-title"
      onClick={onClose}
    >
      <div
        className="modal__card modal__card--split"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="modal__header">
          <div className="modal__header-main">
            <h2 id="upload-complete-title" className="modal__header-title">
              Upload complete
            </h2>
          </div>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
          >
            ×
          </button>
        </header>

        <div className="modal__body">
          <p className="modal__desc">
            {count} document{count === 1 ? " was" : "s were"} added to this case.
            {processing > 0
              ? ` ${processing} ${processing === 1 ? "is" : "are"} still being indexed. You can start asking questions now, and answers will draw on each document as soon as it's ready.`
              : " Everything is indexed and ready for questions."}
          </p>
        </div>

        <footer className="modal__footer">
          <span className="muted modal__footer-hint">
            {readyCount} of {totalDocs} ready
          </span>
          <div className="modal__footer-actions">
            <button type="button" className="btn" onClick={onClose}>
              Stay on Documents
            </button>
            <button type="button" className="btn btn--primary" onClick={onAsk} autoFocus>
              Start asking questions
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// Mirrors the server-side bounds in engine/app/schemas/case.py::CaseUpdate
// (CASE_NAME_MAX / CASE_DESCRIPTION_MAX) so the user gets immediate
// feedback instead of a 422 from FastAPI. Generous on description because
// the demo generator routinely produces 2-3 sentence blurbs of 250-400
// chars, and the underlying column is TEXT.
const CASE_NAME_MAX = 200;
const CASE_DESCRIPTION_MAX = 2000;

function CaseInfoModal({
  caseId,
  name,
  description,
  matterNumber,
  status,
  documentCount,
  readyCount,
  canEdit,
  onUpdated,
  onClose,
}: {
  caseId: number;
  name: string;
  description: string;
  matterNumber?: string | null;
  status: CaseStatus;
  documentCount: number;
  readyCount: number;
  canEdit: boolean;
  onUpdated: (updated: Case) => void;
  onClose: () => void;
}) {
  // Editing state is local to the modal so the parent's `kase` stays the
  // committed truth — we only flow changes back via `onUpdated` after a
  // successful PATCH.
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const [draftDescription, setDraftDescription] = useState(description);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Whenever we re-enter edit mode (or the underlying case changes via a
  // background refresh while the modal is open), reseed the draft from the
  // latest props so the inputs reflect what the user is actually editing.
  useEffect(() => {
    if (editing) {
      setDraftName(name);
      setDraftDescription(description);
      setErr(null);
    }
  }, [editing, name, description]);

  // Esc closes the modal — but only when not in edit mode, so a stray Esc
  // doesn't silently throw away pending edits. In edit mode, Esc cancels
  // the edit instead.
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key !== "Escape") return;
      if (editing) {
        ev.stopPropagation();
        setEditing(false);
        return;
      }
      onClose();
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, editing]);

  const trimmedName = draftName.trim();
  const trimmedDescription = draftDescription.trim();
  const nameChanged = trimmedName !== name.trim();
  const descriptionChanged = trimmedDescription !== description.trim();
  const dirty = nameChanged || descriptionChanged;
  // Name must always be non-empty (DB constraint), but we only enforce the
  // max-length cap on fields the user is actually changing — otherwise a
  // legacy value that happens to be over the cap (e.g. a demo description
  // generated before we tightened the bound) would lock the modal forever.
  const nameValid =
    trimmedName.length > 0 && (!nameChanged || trimmedName.length <= CASE_NAME_MAX);
  const descriptionValid =
    !descriptionChanged || trimmedDescription.length <= CASE_DESCRIPTION_MAX;
  const canSave = !busy && dirty && nameValid && descriptionValid;

  async function handleSave() {
    if (!canSave) return;
    setBusy(true);
    setErr(null);
    try {
      // Only send fields the user actually changed so we don't accidentally
      // touch matter_number/status etc., and so the activity-log entry
      // accurately reflects what was edited.
      const body: { name?: string; description?: string | null } = {};
      if (nameChanged) body.name = trimmedName;
      if (descriptionChanged) {
        // Empty string → null so we clear the column instead of storing "".
        body.description = trimmedDescription.length ? trimmedDescription : null;
      }
      const updated = await api.patch<Case>(`/cases/${caseId}`, body);
      onUpdated(updated);
      setEditing(false);
    } catch (e: any) {
      setErr(e?.message ?? "Failed to save changes.");
    } finally {
      setBusy(false);
    }
  }

  function handleCancelEdit() {
    setEditing(false);
    setDraftName(name);
    setDraftDescription(description);
    setErr(null);
  }

  // Block the backdrop-click close path while editing so a misclick on the
  // overlay can't blow away pending changes — the user has to explicitly
  // Cancel or Save.
  function handleBackdropClick() {
    if (editing) return;
    onClose();
  }

  return (
    <div
      className="modal no-print"
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-info-modal-title"
      onClick={handleBackdropClick}
    >
      <div
        className="modal__card modal__card--lg modal__card--split"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="modal__header">
          <div className="modal__header-main">
            <h2 id="case-info-modal-title" className="modal__header-title">
              {editing ? "Edit case details" : name}
            </h2>
            <div className="modal__header-meta">
              {matterNumber && <span className="case-view__meta-pill">Matter #{matterNumber}</span>}
              <span className="case-view__meta-pill">{readyCount} ready for grounded answers</span>
              <span className="case-view__meta-pill">{documentCount} document{documentCount === 1 ? "" : "s"}</span>
              <span className={`badge badge--${status}`}>{status}</span>
            </div>
          </div>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
          >
            ×
          </button>
        </header>

        <div className="modal__body">
          {editing ? (
            <>
              <div className="modal__section">
                <label className="modal__section-label" htmlFor="case-info-name-input">
                  Name
                </label>
                <input
                  id="case-info-name-input"
                  type="text"
                  value={draftName}
                  onChange={(ev) => setDraftName(ev.target.value)}
                  maxLength={CASE_NAME_MAX}
                  autoFocus
                  disabled={busy}
                />
                <div className="case-info-edit__hint">
                  {trimmedName.length === 0 ? (
                    <span className="case-info-edit__hint-error">Name is required.</span>
                  ) : (
                    <span className="muted">{trimmedName.length}/{CASE_NAME_MAX}</span>
                  )}
                </div>
              </div>
              <div className="modal__section">
                <label className="modal__section-label" htmlFor="case-info-desc-input">
                  Description
                </label>
                <textarea
                  id="case-info-desc-input"
                  value={draftDescription}
                  onChange={(ev) => setDraftDescription(ev.target.value)}
                  maxLength={CASE_DESCRIPTION_MAX}
                  rows={5}
                  disabled={busy}
                />
                <div className="case-info-edit__hint">
                  <span className="muted">{trimmedDescription.length}/{CASE_DESCRIPTION_MAX}</span>
                </div>
              </div>
              {err && <div className="alert alert--error">{err}</div>}
            </>
          ) : (
            <div className="modal__section">
              <div className="modal__section-label">Description</div>
              {description ? (
                <p className="modal__desc">{description}</p>
              ) : (
                <p className="modal__desc muted">
                  No description yet{canEdit ? " — click Edit to add one." : "."}
                </p>
              )}
            </div>
          )}
        </div>

        <footer className="modal__footer">
          <span className="muted modal__footer-hint">
            {editing ? (busy ? "Saving…" : "Editing case details") : "Case details"}
          </span>
          <div className="modal__footer-actions">
            {editing ? (
              <>
                <button
                  type="button"
                  className="btn"
                  onClick={handleCancelEdit}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleSave}
                  disabled={!canSave}
                >
                  {busy ? "Saving…" : "Save"}
                </button>
              </>
            ) : (
              <>
                {canEdit && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </button>
                )}
                <button type="button" className="btn" onClick={onClose}>Close</button>
              </>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
